import { Inject, Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { IAppUserNotificationsService } from '../@theme/shared/components/app-user-notifications/app-user-notifications.service';
import { NotificationItemDTO, NotificationType } from '../@theme/shared/components/app-user-notifications/app-user-notifications.models';

@Injectable({
  providedIn: 'root'
})
export class AppUserNotificationsService implements IAppUserNotificationsService {
  private notifications = signal<NotificationItemDTO[]>([]);

  // Computed signal for unread count
  public unreadNotificationsCount = computed(() =>
    this.notifications().filter((notification: NotificationItemDTO) => !notification.isRead).length
  );

  // Expose notifications as a computed signal to ensure reactivity
  public notifications$ = computed(() => this.notifications());

  constructor(
    @Inject('API_URL') private _apiUrl: string,
    private _http: HttpClient
  ) { }

  /**
   * Load unread notifications from the backend
   * This should be called when the app starts
   */
  public loadNotifications(): Observable<NotificationItemDTO[]> {
    const url: string = `${this._apiUrl}/admin/notifications`;
    return this._http.get<NotificationItemDTO[]>(url).pipe(
      tap((notifications: NotificationItemDTO[]) => {
        notifications.forEach(notification => {
          notification.formatedCreatedDate = this.formatDate(notification.createdDate);
          notification.notificationRedirectRoute = this.getNotificationRedirectionRoute(notification);
        });
        this.notifications.set(notifications);
      })
    );
  }

  /**
   * Refresh notifications from the server
   */
  public refreshNotifications(): void {
    this.loadNotifications().subscribe();
  }

  /**
   * Format date for display
   * @param date Date to format
   */
  private formatDate(date: Date): string {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffMs = now.getTime() - notificationDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else {
      return notificationDate.toLocaleDateString();
    }
  }

  /**
   * Get the redirect route for a notification based on its type
   * @param notification The notification item
   */
  private getNotificationRedirectionRoute(notification: NotificationItemDTO): string {
    switch (notification.type) {
      case NotificationType.Announcement:
        return `/announcements/${notification.typeReferenceId}`;
      default:
        return '/dashboard'; // Default fallback route
    }
  }
}
